import { DataSection } from "components/molecules/Card/CardTypes";
import { ReactNode } from "react";
import { formatMonetaryValue } from "utils/formatting";
import { percentageDifference } from "utils/mathFormulas/mathFormulas.helper";

const prepareCostsCardData = (
    referencePlan: CarbonReductionPlan,
    comparedPlan: CarbonReductionPlan,
    referencePlanName: string,
    comparedPlanName: string
) => {
    // Utility Cost
    const referenceUtilityCost = referencePlan.splitPlanLifetimeEnergyCostTotal;
    const comparedUtilityCost = comparedPlan.splitPlanLifetimeEnergyCostTotal;
    const utilityCostEvolution = percentageDifference(comparedUtilityCost, referenceUtilityCost);

    // Carbon Cost
    const referenceCarbonCost = referencePlan.planLifetimeCarbonPolicyCosts || 0;
    const comparedCarbonCost = comparedPlan.planLifetimeCarbonPolicyCosts || 0;
    const carbonCostEvolution = percentageDifference(comparedCarbonCost, referenceCarbonCost);

    // Total Cost
    const referenceTotalCost = referenceUtilityCost + referenceCarbonCost;
    const comparedTotalCost = comparedUtilityCost + comparedCarbonCost;
    const totalCostEvolution = percentageDifference(comparedTotalCost, referenceTotalCost);

    // ChartData
    const chartCostsData = transformToCostsChartData(referencePlan.annualSummaries, comparedPlan.annualSummaries);

    // Card data
    const { cardBodyData, cardHeaderData } = createCardData(
        referenceUtilityCost,
        referenceCarbonCost,
        referenceTotalCost,
        utilityCostEvolution,
        carbonCostEvolution,
        totalCostEvolution
    );

    // Tooltip
    const tooltipElement: ReactNode = (
        <>
            {comparedPlanName} costs are ${formatMonetaryValue(comparedTotalCost, 0)}
            <br />
            <br />
            {referencePlanName} costs are ${formatMonetaryValue(referenceTotalCost, 0)}
            <br />
            <br />
            There is a {totalCostEvolution.toFixed(0)}% difference between the two plans
        </>
    );
    return {
        cardBodyData,
        cardHeaderData,
        chartCostsData,
        tooltipElement,
    };
};

const createCardData = (
    referenceUtilityCost: number,
    referenceCarbonCost: number,
    referenceTotalCost: number,
    utilityCostEvolution: number,
    carbonCostEvolution: number,
    totalCostEvolution: number
) => {
    const cardBodyData = {
        header: {
            value: "N/A",
            displayInfoKey: "netTotalCost",
            title: "Capital",
        },
        sections: [
            [
                {
                    value: referenceUtilityCost,
                    displayInfoKey: "utilityCost",
                    title: "Utility Cost",
                    increaseDecreasePercentage: utilityCostEvolution,
                },
                {
                    value: referenceCarbonCost,
                    displayInfoKey: "planLifetimeCarbonPolicyCosts",
                    title: "Carbon Cost",
                    valueColor: "neutral",
                    increaseDecreasePercentage: carbonCostEvolution,
                },
            ],
        ],
    };

    const cardHeaderData: DataSection = [
        {
            value: referenceTotalCost,
            displayInfoKey: "netTotalCost",
            title: "Net",
            valueColor: "neutral",
            increaseDecreasePercentage: totalCostEvolution,
        },
    ];

    return {
        cardBodyData,
        cardHeaderData,
    };
};

const transformToCostsChartData = (referenceAnnualSummary: AnnualSummary[], comparedAnnualSummary: AnnualSummary[]) =>
    referenceAnnualSummary
        .map((referenceYear) => {
            const matchingComparedYear = comparedAnnualSummary.find(
                (comparedYear) => comparedYear.year === referenceYear.year
            );

            return {
                utilityCostReference: referenceYear.annualUtilityCost,
                carbonCostReference: referenceYear.carbonPolicyCosts,
                totalCostReference: referenceYear.annualUtilityCost + referenceYear.carbonPolicyCosts,
                utilityCostCompared: matchingComparedYear?.annualUtilityCost,
                carbonCostCompared: matchingComparedYear?.carbonPolicyCosts,
                totalCostCompared:
                    (matchingComparedYear?.annualUtilityCost || 0) + (matchingComparedYear?.carbonPolicyCosts || 0),
                year: referenceYear.year,
            };
        })
        .sort((a, b) => a.year - b.year);

export default prepareCostsCardData;
